import { useState, useEffect, useCallback } from 'react';
import { initializeApp, getApps, getApp, FirebaseApp } from 'firebase/app';
import { 
  getAuth, 
  onAuthStateChanged,
  onIdTokenChanged, 
  User, 
  Auth, 
  signOut, 
  signInWithEmailLink, 
  setPersistence, 
  browserLocalPersistence 
} from 'firebase/auth';
import { firebaseConfig } from '../firebase';
import { setActiveUser } from '../utils/apiClient';

export interface MultiAuthUser {
  appId: string;
  user: User;
  auth: Auth;
}

export interface CachedAuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  emailVerified: boolean;
}

export const STORAGE_KEY_APPS = 'chicagojets_auth_apps';
export const STORAGE_KEY_ACTIVE = 'chicagojets_active_app';
export const STORAGE_KEY_CACHED_USER = 'chicagojets_cached_user';

export function getCachedAuthUser(): CachedAuthUser | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_CACHED_USER);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && parsed.email) {
        return parsed;
      }
    }
  } catch (e) {
    console.warn("Failed to parse cached auth user:", e);
  }
  return null;
}

export function setCachedAuthUser(user: User | null) {
  if (!user) {
    localStorage.removeItem(STORAGE_KEY_CACHED_USER);
    return;
  }
  try {
    localStorage.setItem(STORAGE_KEY_CACHED_USER, JSON.stringify({
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      photoURL: user.photoURL,
      emailVerified: user.emailVerified,
    }));
  } catch (e) {
    console.warn("Failed to cache auth user:", e);
  }
}

export function clearCachedAuthUser() {
  try {
    localStorage.removeItem(STORAGE_KEY_CACHED_USER);
  } catch (e) {}
}

export function hasCachedAuth(): boolean {
  try {
    const active = localStorage.getItem(STORAGE_KEY_ACTIVE);
    const cached = getCachedAuthUser();
    const appsRaw = localStorage.getItem(STORAGE_KEY_APPS);
    if (cached || active) return true;
    if (appsRaw && appsRaw !== '["[DEFAULT]"]' && appsRaw !== '[]') return true;
  } catch (e) {}
  return false;
}

/**
 * Safely gets an existing FirebaseApp or initializes a new one without throwing app/no-app or duplicate-app errors.
 */
export function getOrCreateApp(name: string = '[DEFAULT]'): FirebaseApp {
  const apps = getApps() || [];
  const existing = apps.find(a => a?.name === name);
  if (existing) return existing;
  try {
    return name === '[DEFAULT]' ? initializeApp(firebaseConfig) : initializeApp(firebaseConfig, name);
  } catch (e) {
    try {
      return getApp(name);
    } catch (e2) {
      return (getApps() || [])[0] || (name === '[DEFAULT]' ? initializeApp(firebaseConfig) : initializeApp(firebaseConfig, name));
    }
  }
}

/**
 * Standalone helper to perform sign-in with email link while respecting multi-account state in localStorage.
 * If another account is already logged in, a new FirebaseApp instance is created to preserve existing accounts.
 */
export async function performSignInWithEmailLink(
  email: string,
  link: string,
  existingUsers?: Record<string, MultiAuthUser>
): Promise<{ user: User; appId: string; auth: Auth }> {
  const cleanEmail = email.trim().toLowerCase();

  // Read stored apps from localStorage
  let storedAppIds: string[] = ['[DEFAULT]'];
  try {
    const raw = localStorage.getItem(STORAGE_KEY_APPS);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) {
        storedAppIds = parsed;
      }
    }
  } catch (e) {
    console.warn("Failed to parse stored auth apps:", e);
  }

  let targetAppId: string | null = null;
  let targetAuth: Auth | null = null;

  // 1. If existingUsers has an entry with matching email, reuse its app
  if (existingUsers) {
    const matched = Object.values(existingUsers).find(
      u => u.user?.email?.trim().toLowerCase() === cleanEmail
    );
    if (matched) {
      targetAppId = matched.appId;
      targetAuth = matched.auth;
    }
  }

  // 2. If no existing match, determine whether [DEFAULT] can be used or a new app is needed
  if (!targetAppId || !targetAuth) {
    let isDefaultOccupied = false;
    if (existingUsers && existingUsers['[DEFAULT]']?.user) {
      isDefaultOccupied = existingUsers['[DEFAULT]'].user.email?.trim().toLowerCase() !== cleanEmail;
    } else {
      const storedActive = localStorage.getItem(STORAGE_KEY_ACTIVE);
      if (storedActive) {
        isDefaultOccupied = true;
      }
      try {
        const defApp = getOrCreateApp('[DEFAULT]');
        const defAuth = getAuth(defApp);
        if (defAuth.currentUser && defAuth.currentUser.email?.trim().toLowerCase() !== cleanEmail) {
          isDefaultOccupied = true;
        }
      } catch (e) {}
    }

    const hasOtherExistingUsers = existingUsers && Object.values(existingUsers).some(
      u => u.user && u.user.email?.trim().toLowerCase() !== cleanEmail
    );

    if (isDefaultOccupied || hasOtherExistingUsers) {
      targetAppId = `account-${Date.now()}`;
      const newApp = getOrCreateApp(targetAppId);
      targetAuth = getAuth(newApp);
    } else {
      targetAppId = '[DEFAULT]';
      const defApp = getOrCreateApp('[DEFAULT]');
      targetAuth = getAuth(defApp);
    }
  }

  try {
    await setPersistence(targetAuth, browserLocalPersistence);
  } catch (e) {
    console.warn("Could not set persistence:", e);
  }

  const credential = await signInWithEmailLink(targetAuth, email.trim(), link);
  const loggedInUser = credential.user;

  if (!storedAppIds.includes(targetAppId)) {
    storedAppIds.push(targetAppId);
  }
  localStorage.setItem(STORAGE_KEY_APPS, JSON.stringify(storedAppIds));
  localStorage.setItem(STORAGE_KEY_ACTIVE, targetAppId);
  setActiveUser(loggedInUser);

  return { user: loggedInUser, appId: targetAppId, auth: targetAuth };
}

export function useMultiAuth() {
  const [activeAppId, setActiveAppId] = useState<string | null>(null);
  const [users, setUsers] = useState<Record<string, MultiAuthUser>>({});
  const [loading, setLoading] = useState(true);

  // Initialize from localStorage
  useEffect(() => {
    let storedAppIds: string[] = ['[DEFAULT]'];
    try {
      const stored = localStorage.getItem(STORAGE_KEY_APPS);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) {
          storedAppIds = parsed;
        }
      }
    } catch (e) {
      console.warn("Failed to parse stored auth apps:", e);
    }

    const storedActiveApp = localStorage.getItem(STORAGE_KEY_ACTIVE) || '[DEFAULT]';

    let pendingAuthCount = storedAppIds.length;
    if (pendingAuthCount === 0) {
      setLoading(false);
    }

    storedAppIds.forEach((appId: string) => {
      const appInstance = getOrCreateApp(appId);
      const authInstance = getAuth(appInstance);

      onIdTokenChanged(authInstance, (user) => {
        setUsers(prev => {
          const next = { ...prev };
          if (user) {
            next[appId] = { appId, user, auth: authInstance };
          } else {
            delete next[appId];
          }
          return next;
        });

        pendingAuthCount--;
        if (pendingAuthCount <= 0) {
          setLoading(false);
        }
      });
    });

    setActiveAppId(storedActiveApp);
  }, []);

  // Sync state to localStorage & update apiClient active user
  useEffect(() => {
    if (loading) return;
    const activeIds = Object.keys(users);

    // Ensure activeAppId is valid
    if (activeAppId && !activeIds.includes(activeAppId) && activeIds.length > 0) {
      setActiveAppId(activeIds[0]);
    } else if (activeIds.length === 0) {
      setActiveAppId(null);
    }

    if (activeIds.length > 0) {
      localStorage.setItem(STORAGE_KEY_APPS, JSON.stringify(activeIds));
    } else {
      localStorage.setItem(STORAGE_KEY_APPS, '["[DEFAULT]"]');
    }

    if (activeAppId && users[activeAppId]) {
      localStorage.setItem(STORAGE_KEY_ACTIVE, activeAppId);
      setActiveUser(users[activeAppId].user);
      setCachedAuthUser(users[activeAppId].user);
    } else if (activeIds.length > 0 && users[activeIds[0]]) {
      localStorage.setItem(STORAGE_KEY_ACTIVE, activeIds[0]);
      setActiveUser(users[activeIds[0]].user);
      setCachedAuthUser(users[activeIds[0]].user);
    } else {
      localStorage.removeItem(STORAGE_KEY_ACTIVE);
      setActiveUser(null);
      clearCachedAuthUser();
    }
  }, [users, activeAppId, loading]);

  const switchActiveUser = useCallback((appId: string) => {
    if (users[appId]) {
      setActiveAppId(appId);
      setActiveUser(users[appId].user);
      setCachedAuthUser(users[appId].user);
    }
  }, [users]);

  const prepareNewAccountLogin = useCallback((): Auth => {
    const newAppId = `account-${Date.now()}`;
    const appInstance = getOrCreateApp(newAppId);
    const authInstance = getAuth(appInstance);

    // Listen for login on this new app
    const unsubscribe = onAuthStateChanged(authInstance, (user) => {
      if (user) {
        setUsers(prev => ({ ...prev, [newAppId]: { appId: newAppId, user, auth: authInstance } }));
        setActiveAppId(newAppId);
        setActiveUser(user);
        setCachedAuthUser(user);
        unsubscribe();
      }
    });

    return authInstance;
  }, []);

  const signInWithEmailLinkMulti = useCallback(async (email: string, link: string): Promise<User> => {
    const { user: loggedInUser, appId: targetAppId, auth: targetAuth } = await performSignInWithEmailLink(
      email,
      link,
      users
    );

    // Ensure listener is registered for targetAuth
    onAuthStateChanged(targetAuth, (u) => {
      setUsers(prev => {
        const next = { ...prev };
        if (u) {
          next[targetAppId] = { appId: targetAppId, user: u, auth: targetAuth };
        } else {
          delete next[targetAppId];
        }
        return next;
      });
    });

    // Update state immediately
    setUsers(prev => ({
      ...prev,
      [targetAppId]: { appId: targetAppId, user: loggedInUser, auth: targetAuth }
    }));
    setActiveAppId(targetAppId);
    setActiveUser(loggedInUser);
    setCachedAuthUser(loggedInUser);

    return loggedInUser;
  }, [users]);

  const logoutUser = useCallback(async (appId: string) => {
    const target = users[appId];
    if (target) {
      await signOut(target.auth);
      setUsers(prev => {
        const next = { ...prev };
        delete next[appId];
        return next;
      });
      const remainingIds = Object.keys(users).filter(id => id !== appId);
      if (remainingIds.length > 0) {
        setActiveAppId(remainingIds[0]);
        setActiveUser(users[remainingIds[0]]?.user || null);
        setCachedAuthUser(users[remainingIds[0]]?.user || null);
      } else {
        setActiveAppId(null);
        setActiveUser(null);
        clearCachedAuthUser();
      }
    }
  }, [users]);

  const logoutAll = useCallback(async () => {
    const auths = Object.values(users).map(u => u.auth);
    await Promise.all(auths.map(authInst => signOut(authInst)));
    setUsers({});
    setActiveAppId(null);
    setActiveUser(null);
    clearCachedAuthUser();
    localStorage.removeItem(STORAGE_KEY_APPS);
    localStorage.removeItem(STORAGE_KEY_ACTIVE);
  }, [users]);

  const activeUser = activeAppId ? users[activeAppId] : (Object.values(users)[0] || null);
  if (activeUser?.user) {
    setActiveUser(activeUser.user);
  }
  const allUsers = Object.values(users);

  return {
    activeUser,
    allUsers,
    loading,
    hasCachedAuth: hasCachedAuth(),
    cachedUser: getCachedAuthUser(),
    switchActiveUser,
    prepareNewAccountLogin,
    signInWithEmailLinkMulti,
    logoutUser,
    logoutAll,
    defaultAuth: getAuth(getOrCreateApp('[DEFAULT]'))
  };
}
